import type { MemoActionConflict } from '@ai-meeting/shared'

/**
 * Identifies one disagreement, so dismissing it stays dismissed.
 *
 * The values are part of the key on purpose. A `confirm-items.result` frame arrives every cycle and
 * repeats a conflict that is still unresolved, so keying on the ticket alone would mean one dismissal
 * silenced that ticket for the rest of the meeting. Keying on the values means a dismissal lasts
 * exactly as long as the reader's reason for it: the moment either side moves, it is news again.
 */
export function conflictKey(conflict: MemoActionConflict): string {
  return `${conflict.issue_key}|${conflict.field}|${conflict.current}|${conflict.incoming}`
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const MS_PER_DAY = 86_400_000

/**
 * Whole days between the two sides of a deadline disagreement, or null when that is not a question
 * worth asking.
 *
 * Gated on the field rather than on whether the values happen to parse: a `status` is free text, and
 * a status that reads like a date is still not a date. Guarded by an explicit ISO shape rather than
 * handed to `Date.parse`, which answers for all sorts of things that are not dates. Both sides go
 * through the same parse, so the subtraction needs no timezone care.
 */
export function daysApart(conflict: MemoActionConflict): number | null {
  if (conflict.field !== 'deadline') return null

  const { current, incoming } = conflict
  if (!current || !incoming || !ISO_DATE.test(current) || !ISO_DATE.test(incoming)) return null

  const gap = Math.round(Math.abs(Date.parse(current) - Date.parse(incoming)) / MS_PER_DAY)
  return gap > 0 ? gap : null
}
